import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, cld, cldSrcSet, IMG_SIZES } from "../lib/api";
import { SkeletonText } from "../components/Skeleton";
import { Lightbox } from "../components/Lightbox";

const TONES = ["var(--orange)", "var(--blue)", "var(--lime)"];

const STUDIOS = {
  balboa: {
    key: "balboa",
    tab: "Balboa Recording Studio",
    kicker: "Studio One",
    title: "Balboa Recording Studio",
    heading: "Built for playing, not posing",
    bodyKey: "balboaBody",
    quote: "“Get it right at the source and the rest is just paperwork.”",
    services: [
      "Tracking — full band, live",
      "Overdubs & vocal days",
      "Mixing (in the room or remote)",
      "Analog tape transfers",
      "Demo days / pre-production",
      "Engineer included, always",
    ],
  },
  bungalow: {
    key: "bungalow",
    tab: "Balboa Bungalow",
    kicker: "Studio Two",
    title: "The Bungalow",
    heading: "Smaller room, lower rate, same ears",
    bodyKey: "bungalowBody",
    quote: "“Write it in the small room. Blow it up in the big one.”",
    services: [
      "Writing & topline days",
      "Vocal comps and production",
      "Full tracking, smaller footprint",
      "Beat sessions / programming",
      "Overflow room during Balboa days",
      "Lower day rate, same engineer",
    ],
  },
};

export default function Studio() {
  const [active, setActive] = useState("balboa");
  const [halftone, setHalftone] = useState(false);
  const [copy, setCopy] = useState({ studioIntro: "", balboaBody: "", bungalowBody: "" });
  const [photos, setPhotos] = useState({ balboa: [], bungalow: [] });
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const switchRef = useRef(null);

  const switchStudio = (key) => {
    if (key === active) return;
    setActive(key);
    setLightbox(null);
    switchRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  useEffect(() => {
    let done = 0;
    const settle = () => {
      done += 1;
      if (done >= 2) setLoading(false);
    };
    Promise.all([api.get("/photos?studio=balboa"), api.get("/photos?studio=bungalow")])
      .then(([a, b]) => setPhotos({ balboa: a.data || [], bungalow: b.data || [] }))
      .catch(() => {})
      .finally(settle);
    api.get("/settings")
      .then((r) => {
        setHalftone(!!r.data?.halftoneOn);
        setCopy({
          studioIntro: r.data?.studioIntro || "",
          balboaBody: r.data?.balboaBody || "",
          bungalowBody: r.data?.bungalowBody || "",
        });
      })
      .catch(() => {})
      .finally(settle);
  }, []);

  const s = STUDIOS[active];
  const list = photos[active];
  const cells = list.length ? list : new Array(6).fill(null);

  return (
    <div data-testid="studio-page">
      <div className="wrap page-head">
        <p className="label" style={{ color: "var(--orange)" }}>Two Rooms, One Block Apart</p>
        <h1 className="disp" style={{ marginTop: 12 }}>
          <span className="misreg" data-text="The Rooms">The Rooms</span>
        </h1>
        <p className="measure" style={{ marginTop: 20 }} data-testid="studio-intro">
          {loading ? <SkeletonText lines={2} last="45%" /> : copy.studioIntro}
        </p>
      </div>

      <div className="wrap" ref={switchRef} style={{ scrollMarginTop: 80 }}>
        <div className="studio-switch" data-testid="studio-switch">
          {Object.values(STUDIOS).map((st) => (
            <button
              key={st.key}
              className={`tab studio-tab${active === st.key ? " active" : ""}`}
              onClick={() => switchStudio(st.key)}
              data-testid={`studio-tab-${st.key}`}
            >
              {st.tab}
            </button>
          ))}
        </div>
      </div>

      <div className="wrap studio-panel" data-testid={`studio-panel-${s.key}`}>
        <p className="label" style={{ color: "var(--orange)" }}>{s.kicker}</p>
        <h2 className="disp" style={{ fontSize: "clamp(34px,7vw,72px)", marginTop: 10, marginBottom: 32 }}>
          {s.title}
        </h2>

        <div className="photo-grid" data-testid={`studio-photo-grid-${s.key}`}>
          {loading ? (
            cells.map((p, i) => (
              <div key={i} className={`photo-cell sk${i % 5 === 2 ? " tall" : ""}`} aria-hidden="true" />
            ))
          ) : (
            cells.map((p, i) =>
            p ? (
              <div
                key={p.id}
                className={`${halftone ? "halftone" : "photo-plain"} photo-cell clickable${i % 5 === 2 ? " tall" : ""}`}
                style={{ "--tone": TONES[i % 3] }}
                role="button"
                tabIndex={0}
                aria-label={`Open ${s.tab} photo ${i + 1} full size`}
                onClick={() => setLightbox(i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setLightbox(i);
                  }
                }}
                data-testid={`studio-photo-${s.key}-${i}`}
              >
                <img
                  src={cld(p.url, 900)}
                  srcSet={cldSrcSet(p.url)}
                  sizes={IMG_SIZES}
                  alt={`${s.tab} ${i + 1}`}
                  loading="lazy"
                />
              </div>
            ) : (
              <div
                key={i}
                className={`photo-cell empty${i % 5 === 2 ? " tall" : ""}`}
                style={{ display: "grid", placeItems: "center" }}
                data-testid={`studio-photo-empty-${s.key}-${i}`}
              >
                — add {s.tab} photos in Admin —
              </div>
            ),
          ))}
        </div>

        <section className="sec" style={{ marginTop: 72 }}>
          <div className="two-col">
            <div>
              <h3 className="disp h2">{s.heading}</h3>
              {loading ? (
                <div style={{ marginTop: 18 }}>
                  <SkeletonText lines={5} last="60%" />
                </div>
              ) : (
                (copy[s.bodyKey] || "").split(/\n{2,}/).filter(Boolean).map((p, i) => (
                  <p className="measure" style={{ marginTop: 18 }} key={i} data-testid={`studio-body-${s.key}-${i}`}>
                    {p}
                  </p>
                ))
              )}
            </div>
            <div>
              <div className="misreg-rule" style={{ marginBottom: 28 }} />
              <p className="quote" data-testid={`studio-quote-${s.key}`}>{s.quote}</p>
            </div>
          </div>
        </section>

        <section className="sec" data-testid={`services-list-${s.key}`}>
          <p className="label" style={{ color: "var(--orange)" }}>Services — {s.tab}</p>
          <div className="gear-items" style={{ marginTop: 20 }}>
            {s.services.map((x) => (
              <div className="gear-item" key={x}>
                <span>{x}</span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40 }}>
            <Link to={`/contact?room=${s.key}`} className="btn" data-testid={`studio-book-${s.key}`}>
              Book {s.tab} →
            </Link>
            <button
              className="btn btn-ghost"
              onClick={() => switchStudio(active === "balboa" ? "bungalow" : "balboa")}
              data-testid="studio-see-other"
            >
              See {active === "balboa" ? STUDIOS.bungalow.tab : STUDIOS.balboa.tab} →
            </button>
          </div>
        </section>
      </div>

      <Lightbox
        photos={list}
        index={lightbox}
        label={s.tab}
        onClose={() => setLightbox(null)}
        onIndex={setLightbox}
      />
    </div>
  );
}
